/**
 * AdminShell — layout wrapper for all /admin/* pages.
 *
 * Purpose: Provides sidebar navigation and responsive shell for admin routes.
 * Flow:
 *   - Desktop (≥1280px): Fixed sidebar (240px) + scrollable main area.
 *   - Tablet / Mobile (<1024px): Sidebar hidden → FloatingTopBar pill header +
 *     MobileNavPopup dropdown. Main area gets top padding to clear the bar.
 *
 * RTL-aware: sidebar auto-positions to inline-start; popup anchors to inline-end.
 */
import { Suspense, useState } from "react";
import { Outlet } from "react-router";
import { useTranslation } from "react-i18next";
import { Sidebar } from "@/shared/components/Sidebar";
import { PageSpinner } from "@/shared/components/PageSpinner";
import { FloatingTopBar } from "@/shared/components/FloatingTopBar";
import { MobileNavPopup } from "@/shared/components/MobileNavPopup";
import {
  LayoutDashboard,
  FileText,
  Settings,
  Users,
  Scale,
  Sparkles,
  Building2,
  type LucideIcon,
} from "lucide-react";
import { useLogout, useUserFromToken } from "@/features/auth";
import { useNotificationCenter } from "@/features/notifications";

/* ── Admin nav sections ──────────────────────────────────────── */

const ADMIN_NAV_SECTIONS: { label: string; items: { href: string; icon: LucideIcon; labelKey: string }[] }[] = [
  {
    label: "sidebar.sectionDashboard",
    items: [
      { href: "/admin/dashboard", icon: LayoutDashboard, labelKey: "sidebar.dashboard" },
      { href: "/admin/titles",    icon: FileText,        labelKey: "sidebar.titles" },
      { href: "/admin/publishers", icon: Users,           labelKey: "sidebar.publishers" },
      { href: "/admin/services",  icon: Sparkles,        labelKey: "sidebar.services" },
    ],
  },
  {
    label: "sidebar.management",
    items: [
      { href: "/admin/enterprise", icon: Building2, labelKey: "sidebar.enterprise" },
      { href: "/admin/legal", icon: Scale, labelKey: "sidebar.legal" },
      { href: "/admin/settings", icon: Settings, labelKey: "sidebar.settings" },
    ],
  },
];

/* ── Main export ──────────────────────────────────────────────── */

export function AdminShell() {
  const { t } = useTranslation();
  const logout = useLogout();
  const currentUser = useUserFromToken();
  const { data: notificationData } = useNotificationCenter({ enabled: !!currentUser });
  const [menuOpen, setMenuOpen] = useState(false);

  const userName = currentUser?.fullName || (currentUser?.email?.split("@")[0] ?? "Admin");
  const userEmail = currentUser?.email ?? "";
  const userPhotoUrl = currentUser?.photoUrl ?? null;
  const unreadCount = notificationData?.unreadCount ?? 0;

  const handleSignOut = () => logout.mutate();

  return (
    /*
     * Shell layout:
     * - Mobile/tablet: block flow, full-width, body scrolls naturally
     * - Desktop (xl+): flex row, fixed height 100vh, inner areas scroll
     */
    <div
      className="lg:flex lg:h-screen lg:overflow-hidden"
      style={{ background: "var(--admin-bg)" }}
    >
      {/* ── Desktop sidebar — hidden below xl ── */}
      <div className="hidden lg:flex">
        <Sidebar
          userName={userName}
          userEmail={userEmail}
          userPhotoUrl={userPhotoUrl}
          navSections={ADMIN_NAV_SECTIONS}
          onSignOut={handleSignOut}
          showBell={true}
          unreadNotificationCount={unreadCount}
          isAdmin={true}
        />
      </div>

      {/* ── Mobile/tablet floating top bar ── */}
      <FloatingTopBar
        orgSubtitle={t("sidebar.adminPanel")}
        onMenuClick={() => setMenuOpen((v) => !v)}
        menuOpen={menuOpen}
        unreadCount={unreadCount}
        notificationsHref="/admin/notifications"
      />

      {/* ── Mobile/tablet popup nav ── */}
      <MobileNavPopup
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        userName={userName}
        userEmail={userEmail}
        userPhotoUrl={userPhotoUrl}
        navSections={ADMIN_NAV_SECTIONS}
        onSignOut={handleSignOut}
        isAdmin={true}
      />

      {/* ── Main content area ── */}
      <div className="lg:flex-1 lg:overflow-y-auto">
        {/*
         * Top padding clears the floating bar on mobile/tablet, plus extra
         * breathing room below it. The bar's inset matches the page padding
         * (16px → 24px at sm); minimum clearance is top inset + 52px pill +
         * matching gap (84px mobile / 100px sm). We add ~12px on top of that:
         *   mobile: pt-24 = 96px
         *   sm:     pt-28 = 112px
         * sm:pt-* re-asserts it after sm:p-6 would otherwise override padding-top.
         * lg:pt-8 collapses top pad on desktop where the bar is hidden.
         * Kept in lockstep with AppShell's main padding.
         */}
        <main className="p-4 pt-24 sm:p-6 sm:pt-28 lg:p-8 lg:pt-8">
          {/* Shell-level Suspense keeps the admin sidebar mounted while a lazy
              route chunk loads — only the content area shows the spinner. */}
          <Suspense fallback={<PageSpinner />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
